import type { NotificationEventKind } from './notificationStore'

/**
 * The notices an account can turn off (docs/product/notifications.md's
 * "Preferences"), by who receives them. A group is one choice covering the
 * event kinds that read as one thing to the person, such as the three decisions
 * on a leave request.
 */
export type NotificationGroup = {
  id: string
  label: string
  description: string
  kinds: NotificationEventKind[]
}

const AGENT_GROUPS: NotificationGroup[] = [
  {
    id: 'leave-decisions',
    label: 'Leave request decisions',
    description: 'When your supervisor approves, returns, denies, or reverses a decision on a leave request.',
    kinds: ['leave_request_approved', 'leave_request_returned', 'leave_request_denied', 'leave_request_decision_reversed'],
  },
  {
    id: 'schedule-decisions',
    label: 'Schedule submission decisions',
    description: 'When your supervisor approves, returns, denies, or reverses the approval of your pay period schedule.',
    kinds: ['schedule_submission_approved', 'schedule_submission_returned', 'schedule_submission_denied', 'schedule_submission_decision_reversed'],
  },
  {
    id: 'schedule-published',
    label: 'Schedule published or amended',
    description: 'When your team publishes a pay period, or changes one that was published.',
    kinds: ['schedule_published', 'schedule_amended'],
  },
  {
    id: 'schedule-reminders',
    label: 'Schedule reminders',
    description: 'When your supervisor reminds you to acknowledge a published schedule.',
    kinds: ['schedule_reminder'],
  },
  {
    id: 'availability-decisions',
    label: 'Availability exception decisions',
    description: 'When your supervisor approves, returns, or denies an availability exception.',
    kinds: ['availability_exception_approved', 'availability_exception_returned', 'availability_exception_denied'],
  },
  {
    id: 'open-shift-posted',
    label: 'Open shifts posted',
    description: 'When your supervisor posts an open shift for your team.',
    kinds: ['open_shift_posted'],
  },
  {
    id: 'open-shift-decisions',
    label: 'Open shift claim decisions',
    description: 'When your supervisor approves or denies a claim you made.',
    kinds: ['open_shift_claim_approved', 'open_shift_claim_denied'],
  },
  {
    id: 'giveaway-filled',
    label: 'Shifts you gave away',
    description: 'When a teammate takes a shift you offered.',
    kinds: ['giveaway_filled'],
  },
  {
    id: 'swap-proposals',
    label: 'Shift swap proposals',
    description: 'When a colleague proposes a shift swap with you.',
    kinds: ['shift_swap_proposed'],
  },
  {
    id: 'swap-decisions',
    label: 'Shift swap answers and decisions',
    description: 'When a colleague accepts or declines your proposal, and when your supervisor approves or denies a swap you are in.',
    kinds: ['shift_swap_accepted', 'shift_swap_declined', 'shift_swap_approved', 'shift_swap_denied'],
  },
]

const SUPERVISOR_GROUPS: NotificationGroup[] = [
  {
    id: 'leave-requests',
    label: 'Leave requests',
    description: 'When an agent submits, resubmits, or cancels a leave request.',
    kinds: ['leave_request_submitted', 'leave_request_resubmitted', 'leave_request_cancelled'],
  },
  {
    id: 'schedule-submissions',
    label: 'Schedule submissions',
    description: 'When an agent submits or resubmits a pay period schedule.',
    kinds: ['schedule_submission_submitted', 'schedule_submission_resubmitted'],
  },
  {
    id: 'availability-exceptions',
    label: 'Availability exceptions',
    description: 'When an agent submits or resubmits an availability exception.',
    kinds: ['availability_exception_submitted', 'availability_exception_resubmitted'],
  },
  {
    id: 'open-shift-claims',
    label: 'Open shift claims',
    description: 'When an agent claims an open shift.',
    kinds: ['open_shift_claimed'],
  },
  {
    id: 'giveaways-posted',
    label: 'Shifts given away',
    description: 'When an agent offers one of their shifts to the team.',
    kinds: ['giveaway_posted'],
  },
  {
    id: 'swaps-ready',
    label: 'Shift swaps awaiting approval',
    description: 'When two agents have agreed to swap shifts and it needs your decision.',
    kinds: ['shift_swap_ready'],
  },
]

// A role with no entry receives no notice it can change.
export function notificationGroupsFor(role: string | null): NotificationGroup[] {
  if (role === 'agent') return AGENT_GROUPS
  if (role === 'supervisor') return SUPERVISOR_GROUPS
  return []
}
